import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, ScanSearch, Loader2, Check, ShieldCheck } from "lucide-react";
import { ModelPicker } from "@/components/ModelPicker";

export default function SettingsPage() {
  const [settings, setSettings] = useState(null);
  const [models, setModels] = useState([]);
  const [raviraKey, setRaviraKey] = useState("");
  const [orKey, setOrKey] = useState("");
  const [model, setModel] = useState("google/gemini-2.5-flash");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/settings");
      setSettings(data);
      setModel(data.corrector_model);
    } catch (e) { toast.error("Could not load settings"); }
  };

  useEffect(() => {
    load();
    api.get("/corrector/models").then(({ data }) => setModels(data.models)).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { corrector_model: model };
      if (raviraKey.trim()) payload.ravira_key = raviraKey.trim();
      if (orKey.trim()) payload.openrouter_key = orKey.trim();
      await api.put("/settings/keys", payload);
      setRaviraKey(""); setOrKey("");
      toast.success("Settings saved securely");
      await load();
      const { data } = await api.get("/corrector/models");
      setModels(data.models);
    } catch (e) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-[#f6f5f1]">
      <Header />
      <main className="mx-auto max-w-2xl px-5 py-10 sm:px-8">
        <div className="text-xs uppercase tracking-[0.2em] text-[#d97706] font-mono2">Account</div>
        <h1 className="font-display mt-2 text-3xl tracking-tight">Settings</h1>

        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
          className="mt-8 space-y-6">

          <section className="rounded-2xl border border-[#e6e4dd] bg-white p-6">
            <div className="flex items-center gap-2"><KeyRound className="h-5 w-5 text-[#d97706]" />
              <h2 className="font-display text-xl">API keys</h2></div>
            <p className="mt-1 text-sm text-[#475569]">Stored encrypted. Leave a field blank to keep the current key.</p>

            <div className="mt-5 space-y-5">
              <div className="space-y-1.5">
                <Label>RAVIRA API key {settings?.has_ravira ?
                  <span className="text-[#059669]">· {settings.ravira_key_masked}</span> :
                  <span className="text-[#e11d48]">· not set</span>}</Label>
                <Input data-testid="settings-ravira-key" type="password" value={raviraKey}
                  onChange={(e) => setRaviraKey(e.target.value)} placeholder="rk_live_..." className="h-11 rounded-xl" />
              </div>
              <div className="space-y-1.5">
                <Label>OpenRouter API key {settings?.has_openrouter ?
                  <span className="text-[#059669]">· {settings.openrouter_key_masked}</span> :
                  <span className="text-[#e11d48]">· not set</span>}</Label>
                <Input data-testid="settings-openrouter-key" type="password" value={orKey}
                  onChange={(e) => setOrKey(e.target.value)} placeholder="sk-or-..." className="h-11 rounded-xl" />
              </div>
            </div>
          </section>

          <section className="rounded-2xl border border-[#e6e4dd] bg-white p-6">
            <div className="flex items-center gap-2"><ScanSearch className="h-5 w-5 text-[#d97706]" />
              <h2 className="font-display text-xl">Default corrector model</h2></div>
            <p className="mt-1 text-sm text-[#475569]">Used to verify translation fidelity for new projects.</p>
            <div className="mt-4">
              <ModelPicker models={models} value={model} onChange={setModel} testId="settings-model-select" />
            </div>
          </section>

          <Button data-testid="save-settings-btn" onClick={save} disabled={saving}
            className="h-11 w-full rounded-full bg-[#0f172a] text-white hover:bg-[#1e293b]">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="mr-2 h-4 w-4" /> Save settings</>}
          </Button>

          <p className="flex items-center justify-center gap-1.5 text-xs text-[#94a3b8]">
            <ShieldCheck className="h-3.5 w-3.5" /> Keys are encrypted at rest and never leave your account.
          </p>
        </motion.div>
      </main>
    </div>
  );
}
